import { FeedbackSurface } from '../../../ui/components/FeedbackSurface/FeedbackSurface.tsx';

/**
 * `multiple_choice`'s feedback panel
 * (`../../../features/exercise-types/note-identification/Feedback.tsx`'s
 * pattern, judging on an option index rather than a note name): the same two
 * outcomes, with the value named being the chosen option's TEXT.
 */
export interface TextChoicePublicFeedback {
  readonly correctIndex: number;
  readonly selectedIndex: number;
}

export function isTextChoiceFeedback(value: unknown): value is TextChoicePublicFeedback {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { correctIndex?: unknown; selectedIndex?: unknown };
  return Number.isInteger(candidate.correctIndex) && Number.isInteger(candidate.selectedIndex);
}

interface TextChoiceFeedbackProps {
  feedback: TextChoicePublicFeedback;
  /** The options as presented — the same array the renderer already has from `publicPrompt`. */
  options: readonly string[];
}

export function TextChoiceFeedback({ feedback, options }: TextChoiceFeedbackProps) {
  const { correctIndex, selectedIndex } = feedback;
  const correctText = options[correctIndex] ?? '';
  const selectedText = options[selectedIndex] ?? '';
  const outcome = correctIndex === selectedIndex ? 'correct' : 'miss';

  const title = outcome === 'correct' ? 'Isso mesmo!' : 'Não foi dessa vez';
  const detail =
    outcome === 'correct' ? `A resposta era "${correctText}".` : `Você escolheu "${selectedText}". A resposta era "${correctText}".`;
  const announcement =
    outcome === 'correct'
      ? `Resposta correta. ${correctText}.`
      : `Resposta incorreta. Você escolheu ${selectedText}. A resposta era ${correctText}.`;

  // The same panel for both outcomes — see `../note-identification/Feedback.tsx`.
  return (
    <div data-testid="feedback">
      <FeedbackSurface
        outcome={outcome}
        title={title}
        detail={detail}
        announcement={announcement}
      />
    </div>
  );
}
